import React, {
  createContext,
  useContext,
  useMemo,
  createRef,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import store, { getStoreEntity } from "services/store";
import styled, { css } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCode, faFileLines, faLink } from "@fortawesome/free-solid-svg-icons";
import {
  faPlusCircle,
  faExclamationCircle,
  faBracketsCurly,
  faCircleInfo,
} from "@fortawesome/pro-regular-svg-icons";
import { faEdit } from "@fortawesome/pro-light-svg-icons";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { Tooltip } from "antd";
import ManifestIcon from "assets/icons/manifest.svg?react";
import Fields from "components/ui/Fields";

import Ellipsis from "components/common/Ellipsis";
import ActionMenu from "components/common/ActionMenu";
import Editor from "components/common/Editor";
import Toolbar from "components/common/Editor/Toolbar";
import PackReadMe from "modules/profileBuilder/blocks/components/PackReadMeAsync";
import Accordion from "components/ui/Accordion";
import Link from "components/common/History/Link";
import Icon from "components/ui/Icon";
import Loader from "components/ui/Loader";
import RadioGroup from "components/ui/RadioGroup";
import Button, { TextButton } from "components/ui/Button";
import InvalidClusterProfileIcon from "components/common/ClusterProfilePacks/InvalidClusterProfileIcon";
import PackSystemStateIcon from "components/common/ClusterProfilePacks/PackSystemStateIcon";
import EmptyPage from "components/common/EmptyPage";
import { useClusterProfileColumns } from "components/common/ClusterProfiles";
import Drawer from "components/ui/Drawer";
import FiltersForm from "components/common/ClusterProfilesFilters";
import ClusterProfileVersionMenu from "components/common/ClusterProfileVersionMenu";
import ClusterGroupProfileIcon from "assets/icons/cluster_group_profile.svg?react";

import { Blocks } from "modules/list";
import {
  categorizeProfiles,
  getGroupedVariables,
  getProfilesWithErrors,
} from "modules/profileStack/selectors";
import {
  blue,
  borderColorDefault,
  colorBackgroundAccentCornfieldBlueSubtler,
  concreteWhite,
  darkGray,
  elevationSurfaceSubtlest,
  error,
  lightMidGray,
  lightNeutralNeutral1000,
  lightWhite,
  midGray,
  nearWhite,
  platinum,
  textInverse,
  warning,
  white,
} from "utils/constants/colors";
import { ManifestSchema, PackSchema, PackVersionSchema } from "utils/schemas";
import {
  generateEditorYamlSchema,
  getPackNameComponents,
  getPackPrecedence,
  groupPresetsOptions,
} from "utils/presenters";
import {
  READONLY_K8S_SERVICE_DOMAIN_FIELD,
  READONLY_PACK_DISPLAY_NAME_SCHEMA,
} from "utils/constants/schemas";
import IntegrationPanel from "components/common/ClusterProfiles/IntegrationPanel";
import {
  LAYER_TYPES,
  PROFILE_TYPE_ICONS,
  getEnvironments,
} from "utils/constants";
import BackedUpImage from "components/common/BackedUpImage";
import { ProfileCardTags } from "components/common/ClusterProfileCard";
import { connect, useSelector } from "react-redux";
import { FipsIcon } from "components/common/Fips/FipsIcons";
import { isSecurityModeFips } from "state/auth/selectors";
import {
  getDefaultPresetsFromValues,
  getPreviousPresetDelta,
  parseYAMLValues,
} from "utils/parsers";
import { generateUbuntuAdvantagePresets } from "modules/profileBuilder/utils";
import {
  Context,
  createForm,
  ReadOnlyProvider,
  useFormContext,
} from "modules/form";
import { getUbuntuAdvantagePresets } from "utils/yaml";
import PackStatus from "pages/clusters/overview/components/PackStatus";
import get from "lodash/get";
import {
  SliderVariablesSlot,
  UbuntuProErrorSlot,
} from "components/common/Editor/Presets";
import AlertComponent from "components/common/Alert";
import { isProfileValid } from "utils/domain/profiles";
import { createTeleporter } from "react-teleporter";
import ClusterProfileEmptyInstructions from "./ClusterProfileEmptyInstructions";
import { FieldWrap } from "components/styled/Field";
import { openClusterEditModal } from "state/cluster/actions/edit";
import {
  extractVariablesFromProfiles,
  getFieldPropsFromVariable,
} from "utils/variablesHelpers";
import DocLink from "components/common/DocLink";
import { Variables } from "modules/profileBuilder/blocks/components/Variables";
import { viewVariablesModal } from "./actions";
import { VariableDetailsForm } from "modules/profileBuilder/blocks/components/VariableForm";
import isEmpty from "lodash/isEmpty";

const ProfileStackContext = createContext();
const modalContentRef = createRef();
export const TitleActions = createTeleporter();

const InfoBox = styled(AlertComponent)`
  margin: 16px 16px -26px 16px;
`;
const CATEGORY_TO_COLOR = {
  addons: "rgba(32, 108, 209, 0.1)",
  infra: "rgba(175, 38, 250, 0.1)",
  system: "rgba(255,148,53,0.1)",
};

const ProfileListWrap = styled.div`
  overflow-y: hidden;
  min-width: 400px;
  max-width: 20%;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
`;

const Profiles = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  border-right: 1px solid #ddd;
  flex-grow: 1;
`;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    margin-top: 0px;
    padding: 0px;
  }
  .ant-drawer-body > .popup-container {
    padding: 0px;
  }
`;

const PackWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px;
  padding: 8px 16px;

  background: ${lightWhite};
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid #ddd;
  cursor: pointer;
  background-color: ${white};

  &:hover {
    background: #fafafa;
    box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.08), inset 2px 0px 0px #0072c6;
  }

  ${(props) =>
    props.isSelected &&
    css`
      background: #fafafa;
      box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.08), inset 2px 0px 0px #0072c6;
    `}
`;

const ProfileTitleWrap = styled.div`
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  overflow: hidden;

  div[role="menu"] {
    min-width: 24px;
    justify-content: center;
    position: relative;
    display: flex;
    align-items: center;
  }
`;

const ProfileNameWrap = styled.div`
  display: flex;
  overflow: hidden;
  align-items: center;
  span {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const SidebarTitle = styled.div`
  line-height: 45px;
  padding: 0 12px;
  color: #fff;
  border-right: 1px solid #0d1440;
  background-color: #30365b;
  display: flex;
  justify-content: space-between;
`;

const AddProfileWrap = styled.div`
  font-size: 16px;
  color: ${blue};
`;

const ArrowWrapper = styled.div`
  display: inline-block;

  svg {
    color: ${blue};
    opacity: 0.1;
  }

  ${(props) =>
    props.isSelected &&
    css`
      svg {
        opacity: 1;
      }
    `}
`;

const LogoWrap = styled.div`
  margin-right: 10px;
  width: 30px;
  min-width: 30px;

  > img {
    max-width: 100%;
  }
`;

const PackDetails = styled.div`
  display: flex;
  align-items: center;
  min-width: 0px;
  margin: 0 5px 0 10px;
`;

const ExclamationIcon = styled(FontAwesomeIcon)`
  color: ${warning};
  margin-right: 5px;
`;

const ProfileCategory = styled.div`
  font-weight: 500;
  font-size: 10px;
  line-height: 10px;
  height: 34px;
  line-height: 34px;
  text-transform: uppercase;
  padding: 0 12px;
  letter-spacing: 0.05em;
  display: flex;
  justify-content: space-between;
  margin: 8px 0 1px;

  ${(props) =>
    props.type &&
    CATEGORY_TO_COLOR[props.type] &&
    css`
      background: ${CATEGORY_TO_COLOR[props.type]};
    `};

  &:first-child {
    margin-top: 0;
  }
`;

const LinkWrap = styled.div`
  color: ${blue};
  font-size: 11px;
  margin: 0 7px;
  flex-grow: 1;
`;

const ProfilePackList = styled.div`
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.2;
      pointer-events: none;
    `}
`;

const AttachedManifestDetails = styled.div`
  display: flex;
  align-items: center;
`;

const AttachedManifestContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${white};
  padding: 4px 8px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.08);
  margin-left: 16px;
  cursor: pointer;
  border-bottom: 1px solid ${nearWhite};

  svg {
    margin-right: 8px;
  }

  ${(props) =>
    props.selectedManifest &&
    css`
      background: linear-gradient(
          0deg,
          rgba(32, 108, 209, 0.07),
          rgba(32, 108, 209, 0.07)
        ),
        #ffffff;
      box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.08), inset 2px 0px 0px #206cd1;
    `}
`;

const AttachedManifestIcon = styled(ManifestIcon)`
  position: absolute;
  top: 0;
  left: 0;
  height: 14px;
`;

const EmptyProfilesContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${white};
  > ${SidebarTitle} {
    margin: 0;
  }
`;

const EmptyPageWrap = styled.div`
  padding: 0 0 0 64px;
  flex-grow: 1;
`;

const StyledAttachButton = styled(Button)`
  margin-bottom: 30px;
`;

const ModalContentWrapper = styled.div`
  min-height: 100%;
`;

const Warning = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  background: #fdf6f0;
  border: 1px solid #f9ddc4;
  border-radius: 4px;
  padding: 10px;
  margin: 10px;
  font-size: 12px;

  > .anticon {
    color: #efa96b;
    font-size: 16px;
    margin-right: 4px;
  }
`;

const ProfileActionsWrap = styled.div`
  display: flex;
  align-items: center;
`;

const FIPSIconWrapper = styled.span`
  height: 48px;
  cursor: pointer;
  padding: 14px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

const ClusterGroupInfo = styled.span`
  background: ${colorBackgroundAccentCornfieldBlueSubtler};
  border-radius: 15px;
  display: flex;
  height: 24px;
  padding: 0px var(--space-075, 6px);
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 8px;
`;

const ClusterGroupTooltip = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledText = styled.span`
  color: ${(props) => props.color || white};
  font-weight: ${(props) => props.weight || 100};
  line-break: unset;
  white-space: nowrap;
`;

const StyledTooltip = styled(Tooltip)`
  max-width: none !important;
  .ant-tooltip-inner {
    max-width: none !important;
  }
  .ant-tooltip {
    max-width: none !important;
  }
`;

const StyledActionMenu = styled(ActionMenu)`
  width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const tooltipStyle = {
  maxWidth: "none", // Set your custom max-width value here
};

const StyledVariablesToolbarWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-basis: 48px;
  padding: 0 12px;
  align-items: center;
  border-right: 1px solid ${lightMidGray};
`;
const BracketsIcon = styled(Icon)`
  margin-right: 4px;
`;
const InfoIcon = styled(Icon)`
  margin-left: 8px;
`;
const ConfigureButton = styled(Button)`
  background: ${concreteWhite};
  border: 1px solid ${platinum};
  color: ${darkGray};

  :hover,
  :focus {
    background-color: ${white};
    color: ${midGray};
  }
`;

function ProfileList({
  isSecurityMode,
  clusterUid,
  openClusterEditModal,
  hasChanges,
}) {
  const {
    profiles,
    expanded,
    selectedLayer,
    markForDeletion,
    errors,
    options,
  } = useContext(ProfileStackContext).state;

  const {
    addProfile,
    keepProfile,
    removeProfile,
    replaceProfile,
    selectLayer,
    toggleExpand,
    onManifestSelect,
    onVersionChange,
  } = useContext(ProfileStackContext).actions;
  const { t } = useTranslation();
  const allActionsDisabled =
    useContext(ProfileStackContext).mode === "read-only";
  const profileCategories = useMemo(
    () => categorizeProfiles(profiles, markForDeletion),
    [profiles, markForDeletion]
  );

  function renderPack(pack, profileGuid, profileUid) {
    const isSelected = selectedLayer === pack.guid;
    const packErrors = errors[pack.guid];
    const packType = pack?.type || pack?.spec?.type;
    // TODO: Refactor pack and pack version schemas
    const packManifests = pack?.manifests?.length > 0 && pack?.manifests;
    const manifests =
      packManifests || pack.spec.manifests || pack?.pack?.manifests || [];
    const packName =
      getPackNameComponents(pack?.spec?.name).length > 0
        ? pack.spec.name
        : pack?.spec?.displayName;

    const packDetails = `${packName || pack.name || ""} ${pack?.spec?.version}`;

    function renderLayerIcon() {
      if (packType === "manifest") {
        return <ManifestIcon />;
      }

      const layerType =
        pack?.spec?.addonSubType || pack?.spec?.addonType || pack?.spec?.layer;

      let packLogo = pack?.spec?.logoUrl;
      if (pack.spec.type === "oci") {
        packLogo = packLogo || `/v1/packs/${pack.metadata.uid}/logo`;
      }

      const layerIconSrc = LAYER_TYPES.find(
        ({ type }) => type === layerType
      )?.icon;

      return <BackedUpImage src={packLogo} fallbackSrc={layerIconSrc} />;
    }

    function renderManifests() {
      return (manifests || []).map((manifest) => {
        const isManifestSelected = selectedLayer === manifest.guid;
        return (
          <AttachedManifestContainer
            key={manifest.name}
            onClick={() => {
              onManifestSelect({
                manifestGuid: manifest.guid,
                layerGuid: pack.guid,
                profileGuid,
                editMode: options.editMode,
              });
            }}
            selectedManifest={isManifestSelected}
          >
            <AttachedManifestDetails>
              <ManifestIcon />
              {manifest.name}
            </AttachedManifestDetails>
            {packType === "manifest" && (
              <ArrowWrapper isSelected={isManifestSelected}>
                <Icon awesome={faChevronRight} />
              </ArrowWrapper>
            )}
          </AttachedManifestContainer>
        );
      });
    }

    function onLayerClick() {
      if (packType === "manifest") {
        onManifestSelect({
          manifestGuid: manifests[0].guid,
          layerGuid: pack.guid,
          profileGuid,
          isAttachedManifest: false,
          editMode: options.editMode,
        });
        return;
      }

      selectLayer(pack.guid, profileGuid);
    }

    return (
      <React.Fragment key={pack.guid}>
        <PackWrap
          data-qa={packDetails}
          data-qa-type={packType}
          data-qa-uid={pack.uid || pack.metadata?.uid}
          isSelected={isSelected}
          onClick={onLayerClick}
        >
          <LeftSection>
            {manifests.length > 0 && packType !== "manifest" && (
              <AttachedManifestIcon />
            )}
            {options.editMode && (
              <PackStatus
                pack={pack}
                profileUid={profileUid}
                clusterUid={clusterUid}
              />
            )}
            <Tooltip placement="right" title={packDetails}>
              <PackDetails>
                <LogoWrap>{renderLayerIcon()}</LogoWrap>
                <Ellipsis>{packDetails}</Ellipsis>
              </PackDetails>
            </Tooltip>
            <div>
              <PackSystemStateIcon
                systemState={pack?.spec?.annotations?.system_state}
              />
              {packErrors?.length && (
                <Tooltip
                  placement="right"
                  title={packErrors.map((error, index) => (
                    <div key={`error-${pack.guid}-${index}`}>
                      {error.message}
                    </div>
                  ))}
                >
                  <ExclamationIcon icon={faExclamationCircle} />
                </Tooltip>
              )}
            </div>
          </LeftSection>
          <RightSection>
            {isSecurityMode && (
              <FIPSIconWrapper>
                <FipsIcon fipsStatus={pack?.spec?.annotations?.fips} />
              </FIPSIconWrapper>
            )}
            <ArrowWrapper isSelected={isSelected}>
              <Icon awesome={faChevronRight} />
            </ArrowWrapper>
          </RightSection>
        </PackWrap>
        {renderManifests()}
      </React.Fragment>
    );
  }

  function renderProfile(profile, profileType) {
    let { versions, version } = profile?.spec || profile?.specSummary || {};

    if (profile?.profile?.spec?.versions) {
      versions = profile.profile.spec.versions;
    }

    const scope = profile.metadata?.annotations?.scope;

    const isClusterGroupProfile =
      profile?.spec?.relatedObject?.kind === "clustergroup";

    const replaceOption = {
      label: "Replace profile",
      onClick: () => replaceProfile(profile, "spectrocluster"),
      disabled: allActionsDisabled,
    };

    let menuOptions = [
      {
        label: "Remove profile",
        onClick: () => removeProfile(profile),
        disabled: allActionsDisabled,
      },
      replaceOption,
    ];

    if (
      ["cluster", "infra"].includes(profile.type) &&
      options.allowInfraProfileRemoval === false
    ) {
      menuOptions = [replaceOption];
    }

    if (profile.markedForDeletion) {
      menuOptions = [
        {
          label: "Keep profile",
          onClick: () => keepProfile(profile),
          disabled: allActionsDisabled,
        },
      ];
    }

    let versionInfo = null;
    if (version) {
      versionInfo = `(${version})`;
    }
    if (versions?.length) {
      versionInfo = (
        <ClusterProfileVersionMenu
          minWidth="100px"
          height="24px"
          canCreate={false}
          selectedVersion={version}
          versions={versions}
          disabled={allActionsDisabled}
          onVersionChange={(props) =>
            onVersionChange({
              ...props,
              profile,
              profileType: profile.type,
            })
          }
        />
      );
    }

    const orderedPacks = [...profile.spec.published.packs].sort(
      (packA, packB) => getPackPrecedence(packB) - getPackPrecedence(packA)
    );

    const isOnClusterGroupRoute = store
      .getState()
      .location?.route?.includes("CLUSTER_GROUPS");

    const renderTooltipForClusterGroup = () => (
      <StyledTooltip
        style={tooltipStyle}
        placement="right"
        title={
          <ClusterGroupTooltip>
            <StyledText>{t("Cluster Group Add-On Profile")}</StyledText>
          </ClusterGroupTooltip>
        }
      >
        <ClusterGroupInfo>
          <ClusterGroupProfileIcon></ClusterGroupProfileIcon>
          <StyledText color="#545f7e" weight="600">
            {profile?.spec?.relatedObject?.name}
          </StyledText>
        </ClusterGroupInfo>
      </StyledTooltip>
    );

    const renderProfileActions = () => (
      <>
        {versionInfo}
        <StyledActionMenu options={menuOptions} trigger={["click"]} />
      </>
    );

    return (
      <Accordion.Panel
        key={profile.guid}
        header={
          <ProfileTitleWrap
            disabled={profile.markedForDeletion}
            data-qa={profile.metadata.name}
            data-qa-type={profileType}
            data-qa-uid={profile.metadata.uid}
          >
            <ProfileNameWrap>
              <span title={profile.metadata.name}>{profile.metadata.name}</span>
              {profile.linked ? (
                <LinkWrap>
                  <Tooltip
                    placement="top"
                    title={t("Profile has {{ profileType }} layers", {
                      profileType:
                        profileType !== "add-on" ? "add-on" : "infrastructure",
                    })}
                  >
                    <Icon awesome={faLink} />
                  </Tooltip>
                </LinkWrap>
              ) : null}
              {isOnClusterGroupRoute
                ? renderTooltipForClusterGroup()
                : isClusterGroupProfile
                ? renderTooltipForClusterGroup()
                : null}
              <InvalidClusterProfileIcon
                isInvalid={profile.metadata.annotations?.isInValid}
                invalidReason={profile.metadata.annotations?.inValidReason}
              />
            </ProfileNameWrap>
            <ProfileActionsWrap onClick={(e) => e.stopPropagation()}>
              <ProfileCardTags scope={scope} compressed />
              {isOnClusterGroupRoute
                ? renderProfileActions()
                : !isClusterGroupProfile
                ? renderProfileActions()
                : null}
            </ProfileActionsWrap>
          </ProfileTitleWrap>
        }
      >
        <ProfilePackList disabled={profile.markedForDeletion}>
          {orderedPacks.map((pack) =>
            renderPack(pack, profile.guid, profile.metadata.uid)
          )}
        </ProfilePackList>
      </Accordion.Panel>
    );
  }

  function renderSystemGroup() {
    if (!options?.allowSystemProfiles) {
      return null;
    }
    let accordion = null;

    if (options?.isSystemProfileRequired) {
      accordion = (
        <Warning>
          <Icon awesome={faExclamationCircle} />
          Please add a system profile to continue
        </Warning>
      );
    }

    let addButton = (
      <AddProfileWrap>
        <Tooltip title={t("Add system profile")} placement="right">
          <Icon awesome={faPlusCircle} onClick={addProfile("system")} />
        </Tooltip>
      </AddProfileWrap>
    );
    if (profileCategories.system) {
      accordion = (
        <Accordion
          variant="white"
          withActions
          activeKey={expanded}
          onChange={toggleExpand}
        >
          {renderProfile(profileCategories.system, "system")}
        </Accordion>
      );
      addButton = null;
    }

    return (
      <>
        <ProfileCategory type="system">
          {t("System layers")}
          {addButton}
        </ProfileCategory>
        {accordion}
      </>
    );
  }

  function renderConfigureButton() {
    if (hasChanges) {
      return (
        <Tooltip
          placement="top"
          title={t(
            "To configure your new variables values proceed to review changes."
          )}
        >
          <InfoIcon awesome={faCircleInfo} />
        </Tooltip>
      );
    }

    return (
      <ConfigureButton
        data-qa="configure-values"
        secondary
        disabled={hasChanges}
        onClick={() =>
          openClusterEditModal({
            menuItem: "profileVariablesConfig",
          })
        }
      >
        {t("Configure Values")}
      </ConfigureButton>
    );
  }

  function renderProfileVariablesToolbar() {
    const variables = extractVariablesFromProfiles(profiles);
    const numberOfVars = variables.length;
    if (!numberOfVars) {
      return null;
    }

    const noOfHiddenVariables = variables.filter(
      (variable) => variable.hidden
    ).length;

    function renderAction() {
      if (options.editMode) {
        return renderConfigureButton();
      }

      return <DocLink location="profile-variables" text={t("Learn more")} />;
    }
    return (
      <StyledVariablesToolbarWrapper>
        <div>
          <BracketsIcon awesome={faBracketsCurly} />
          <span>{`${numberOfVars} Profile ${
            numberOfVars > 1 ? "variables" : "variable"
          } ${
            noOfHiddenVariables ? `(${noOfHiddenVariables} hidden)` : ""
          }`}</span>
        </div>
        {renderAction()}
      </StyledVariablesToolbarWrapper>
    );
  }

  return (
    <ProfileListWrap>
      <SidebarTitle>
        {t("Cluster profiles")}
        <TitleActions.Target />
      </SidebarTitle>
      <Profiles>
        {renderSystemGroup()}
        <ProfileCategory type="addons">
          {t("Addon layers")}
          <AddProfileWrap>
            <Tooltip title={t("Add add-on profile")} placement="right">
              <TextButton
                data-qa="add-addon-profile"
                icon={
                  <Icon
                    aria-label={t("Add add-on profile")}
                    awesome={faPlusCircle}
                  />
                }
                onClick={addProfile("add-on", [], "spectrocluster")}
                disabled={allActionsDisabled}
              />
            </Tooltip>
          </AddProfileWrap>
        </ProfileCategory>
        <Accordion
          withActions
          variant="white"
          activeKey={expanded}
          onChange={toggleExpand}
        >
          {profileCategories.addons.map((addonProfile) =>
            renderProfile(addonProfile, "add-on")
          )}
        </Accordion>
        {profileCategories.infra ? (
          <>
            <ProfileCategory type="infra">
              {t("Infrastructure layers")}
            </ProfileCategory>
            <Accordion
              variant="white"
              withActions
              activeKey={expanded}
              onChange={toggleExpand}
            >
              {renderProfile(profileCategories.infra, "infra")}
            </Accordion>
          </>
        ) : null}
      </Profiles>
      {renderProfileVariablesToolbar()}
    </ProfileListWrap>
  );
}

const EditorTitleWrap = styled.div`
  display: flex;

  > span {
    margin-left: 10px;
    line-height: 24px;
  }
`;

const ConfigPanel = styled.div`
  background-color: #fff;
  min-height: 100%;
  width: 100%;

  > div:nth-child(2) {
    padding: 0 24px;
  }

  .ant-radio-wrapper {
    display: block;
  }
`;

const ToolbarButtons = styled.div`
  > div {
    display: flex;
    margin-right: 15px;
    border-radius: 3px;
    overflow: hidden;
  }
`;

const ToolbarButton = styled(TextButton)`
  color: #afabea;
  border: 1px solid #918cd9;
  border-radius: 0px;

  &:first-child {
    border-width: 1px 0px 1px 1px;
    border-radius: 3px 0px 0px 3px;
  }

  &:last-child {
    border-width: 1px 1px 1px 0;
    border-radius: 0px 3px 3px 0px;
  }

  &:hover,
  &:focus {
    color: #afabea;
    border-color: #918cd9;
  }

  ${(props) =>
    props.selected &&
    css`
      color: #515e71;
      background: #fff;
      border: 1px solid #fff;
      &:hover,
      &:focus {
        color: #515e71;
        background: #fff;
        border-color: transparent;
      }
    `}
`;

const StyledVariableDrawer = styled(Drawer)`
  .ant-drawer-body > .popup-container {
    padding: 0;
  }
`;

function getFormUpdatesOnUbuntuAdvantageChange(
  { value, name, group },
  { currentPresets, currentValues, defaultValues }
) {
  const shouldUpdateCurrentValues =
    currentPresets?.ubuntuAdvantage?.enabled || name === "enabled";
  const updatedPresets = {
    ...(currentPresets || {}),
    ubuntuAdvantage: {
      ...currentPresets.ubuntuAdvantage,
      [name]: value,
    },
  };

  if (!shouldUpdateCurrentValues) {
    return {
      values: currentValues,
      presets: updatedPresets,
    };
  }

  const presets = generateUbuntuAdvantagePresets(
    updatedPresets.ubuntuAdvantage
  );
  const presetValues = getUbuntuAdvantagePresets(
    { field: name, value, base: currentValues },
    updatedPresets.ubuntuAdvantage
  );
  let base =
    getPreviousPresetDelta({
      presetValues: currentPresets,
      values: currentValues,
      presets,
      group,
      defaultValues,
    }) || currentValues;

  const updatedValues = parseYAMLValues(base, presetValues);
  return {
    values: typeof updatedValues === "string" ? updatedValues : currentValues,
    presets: updatedPresets,
  };
}

function getFormUpdatesOnPresetsChange(newData, currentData) {
  const { value, group, presets } = newData;
  const { currentPresets, currentValues, defaultValues } = currentData;

  if (group === "ubuntuAdvantage") {
    return getFormUpdatesOnUbuntuAdvantageChange(newData, currentData);
  }

  const selectedPresets = presets.find(
    (preset) => preset.group === group && preset.value === value
  );

  let base =
    getPreviousPresetDelta({
      presetValues: currentPresets,
      values: currentValues,
      presets,
      group,
      defaultValues,
    }) || currentValues;

  const updatedValues = parseYAMLValues(base, selectedPresets) || currentValues;
  const updatedPresets = {
    ...(currentPresets || {}),
    [group]: value,
  };

  return {
    values: updatedValues,
    presets: updatedPresets,
  };
}

function PackValuesEditor({ isSecurityMode }) {
  const { t } = useTranslation();
  const {
    values,
    selectedLayer,
    selectedProfile,
    resolvedValues,
    profiles,
    macros,
    integrationFormValues,
    formMode,
    options,
  } = useContext(ProfileStackContext).state;

  const {
    onValuesChange,
    onIntegrationFieldChange,
    changeFormMode,
    onPresetsChange,
    onViewVariables,
  } = useContext(ProfileStackContext).actions;
  const allActionsDisabled =
    useContext(ProfileStackContext).mode === "read-only";
  const environments = useSelector(getEnvironments);
  const helpContent = {
    title: t("What are profile variables?"),
    docLocation: "profile-variables",
  };

  const cloudType = useMemo(() => {
    const infraProfile = profiles.find((profile) => {
      return ["full", "cluster"].includes(
        profile.spec?.published?.type || profile.spec?.type
      );
    });
    return (
      infraProfile?.spec?.published?.cloudType || infraProfile?.spec?.cloudType
    );
  }, [profiles]);

  const selectedPack = useMemo(() => {
    let pack =
      getStoreEntity(selectedLayer, PackSchema) ||
      getStoreEntity(selectedLayer, ManifestSchema);

    if (!pack) {
      const profile = profiles.find((profile) =>
        profile.spec.published.packs.find((pack) => pack.guid === selectedLayer)
      );
      pack =
        (profile?.spec?.published?.packs || []).find(
          (pack) => pack.guid === selectedLayer
        ) || getStoreEntity(selectedLayer, PackVersionSchema);
    }

    if (!pack) {
      return null;
    }

    const resolved = resolvedValues.find((value) => {
      const profile = profiles.find((profile) =>
        profile.spec.published.packs.find(
          (publishedPack) =>
            publishedPack.metadata.uid ===
            (pack?.uid || pack?.metadata?.uid || pack?.pack?.metadata?.uid)
        )
      );
      return profile?.metadata.uid === value.uid;
    })?.resolved;

    return {
      ...pack,
      schema: generateEditorYamlSchema(
        [
          ...(pack?.spec?.schema || []),
          READONLY_PACK_DISPLAY_NAME_SCHEMA,
          options.editMode &&
            pack?.spec?.layer === "k8s" &&
            READONLY_K8S_SERVICE_DOMAIN_FIELD,
        ].filter(Boolean)
      ),
      resolved,
    };
  }, [selectedLayer, resolvedValues, profiles, options.editMode]);

  const profileVariables = useMemo(() => {
    const selectedProfileDetails =
      profiles.find((profile) => profile.guid === selectedProfile) || {};

    const packs = selectedProfileDetails?.spec?.published?.packs || [];

    const packValues = packs.reduce((accum, pack) => {
      const { guid: packGuid = "", spec = {} } = pack;
      const { manifests = [], displayName = "" } = spec;
      manifests?.forEach((manifest) => {
        const manifestValue =
          values[manifest.guid] || manifest.content || manifest.values || "";
        accum.push({
          parentName: displayName,
          name: manifest.name,
          values: manifestValue,
        });
      });
      accum.push({
        name: displayName,
        values: values[packGuid],
      });

      return accum;
    }, []);

    return (selectedProfileDetails?.variables || []).map((variable) => {
      const usedIn = packValues.reduce((accum, value) => {
        const { name, values, parentName } = value;
        const regex = new RegExp(
          `{{\\s*\\.spectro\\.var\\.${variable.name}\\s*}}`,
          "g"
        );
        const matches = values?.match(regex) || [];
        if (matches.length) {
          accum.push({
            name,
            parentName,
            occurrences: matches.length,
          });
        }
        return accum;
      }, []);
      return {
        ...variable,
        usedIn,
      };
    });
  }, [selectedProfile, profiles, values]);

  const isIntegration =
    !!selectedPack?.spec?.template?.parameters?.inputParameters?.length;

  const readme = selectedPack?.spec?.readme;

  const integrationValues = useMemo(() => {
    return integrationFormValues[selectedLayer];
  }, [selectedLayer, integrationFormValues]);

  const presets = useMemo(() => {
    const managedCloudTypes = environments
      .filter((env) => env.isSupervised)
      .map((env) => env.apiKey);
    const isUbuntu = selectedPack?.spec?.name?.includes("ubuntu");
    const isManagedCloudType = managedCloudTypes.includes(cloudType);
    const showUbuntuAdvantage =
      !isManagedCloudType && selectedPack?.spec?.layer === "os" && isUbuntu;
    const selectedPresets =
      values?.presets?.[selectedLayer] ||
      getDefaultPresetsFromValues(values?.[selectedLayer], showUbuntuAdvantage);

    const ubuntuPresets = generateUbuntuAdvantagePresets(
      selectedPresets?.ubuntuAdvantage
    );

    return {
      ...groupPresetsOptions(selectedPack?.spec?.presets),
      ...groupPresetsOptions(ubuntuPresets),
    };
  }, [selectedPack, cloudType, values, selectedLayer, environments]);

  const onPacksEditorPresetsChange = useCallback(
    (data) => {
      const { values: newValues, presets: newPresets } =
        getFormUpdatesOnPresetsChange(data, {
          currentPresets: values?.presets?.[selectedLayer],
          currentValues: values?.[selectedLayer],
          defaultValues: selectedPack?.spec?.values,
        });

      onValuesChange(newValues);
      onPresetsChange(newPresets);
    },
    [values, selectedLayer, selectedPack, onPresetsChange, onValuesChange]
  );

  const isSelectedPackFipsEnabled = selectedPack?.annotations?.fips === "true";
  let fieldErrors = {};

  const isUbuntuProErrorBeVisible = (errors) =>
    Object.keys(errors).some((key) => !!fieldErrors[key]);

  const configuredPresets = get(
    values,
    `presets.${selectedLayer}.ubuntuAdvantage`
  );

  if (isSecurityMode && isSelectedPackFipsEnabled) {
    fieldErrors[`presets.${selectedLayer}.ubuntuAdvantage.enabled`] =
      configuredPresets?.enabled ? false : ["Required field"];
    fieldErrors[`presets.${selectedLayer}.ubuntuAdvantage.token`] =
      configuredPresets?.token?.trim()?.length ? false : ["Required field"];
    fieldErrors[`presets.${selectedLayer}.ubuntuAdvantage.fips`] =
      configuredPresets?.fips ? false : ["Required field"];
  }

  const validateUbuntuProErrors = () => {
    if (isSecurityMode && isUbuntuProErrorBeVisible(fieldErrors)) {
      <UbuntuProErrorSlot.Source>
        <InfoBox
          banner
          description={t(
            "Ubuntu Pro, Token and fips are required for FIPS compatibility."
          )}
          type="warning"
          showIcon
        />
      </UbuntuProErrorSlot.Source>;
    }
  };

  function renderTitle() {
    if (!selectedPack) {
      return "";
    }

    return (
      <EditorTitleWrap>
        <span>
          {selectedPack?.spec?.displayName || selectedPack.name}{" "}
          {selectedPack?.spec?.version || ""}
        </span>
      </EditorTitleWrap>
    );
  }

  function renderCustomToolbarButtons() {
    return (
      <ToolbarButtons onClick={(ev) => ev.stopPropagation()}>
        <RadioGroup value={formMode} onChange={changeFormMode}>
          {isIntegration ? (
            <ToolbarButton data-qa="config" value="config">
              <Icon awesome={faEdit} />
              {t("Properties")}
            </ToolbarButton>
          ) : null}

          {readme ? (
            <ToolbarButton data-qa="readme" value="readme">
              <Icon awesome={faFileLines} />
              Readme
            </ToolbarButton>
          ) : null}

          <ToolbarButton data-qa="packs" value="packs">
            <Icon awesome={faCode} />
            {t("Values")}
          </ToolbarButton>
        </RadioGroup>
      </ToolbarButtons>
    );
  }

  if (formMode === "config") {
    return (
      <ConfigPanel>
        <Toolbar
          title={renderTitle()}
          customToolbarButtons={renderCustomToolbarButtons()}
        />
        <ReadOnlyProvider isActive={allActionsDisabled}>
          <IntegrationPanel
            inputParameters={
              selectedPack?.spec?.template?.parameters?.inputParameters
            }
            dependencies={selectedPack?.spec?.dependencies || []}
            formData={integrationValues}
            onFieldChange={onIntegrationFieldChange}
          />
        </ReadOnlyProvider>
      </ConfigPanel>
    );
  }

  if (formMode === "readme") {
    return (
      <PackReadMe
        markdownContent={readme}
        title={renderTitle()}
        customToolbarButtons={renderCustomToolbarButtons()}
      />
    );
  }

  function renderActions(variable) {
    return (
      <ActionMenu
        options={[
          { label: t("View"), onClick: () => onViewVariables(variable) },
        ]}
      />
    );
  }

  return (
    <Context.Provider
      value={{
        data: values,
        fieldErrors: {
          presetTittleError: isUbuntuProErrorBeVisible(fieldErrors),
          ...fieldErrors,
        },
        onFieldChange() {},
        onFieldBlur() {},
      }}
    >
      <ReadOnlyProvider isActive={allActionsDisabled}>
        {validateUbuntuProErrors()}
        <SliderVariablesSlot.Source>
          <Variables
            variables={profileVariables}
            allowCreate={false}
            actions={renderActions}
          />
        </SliderVariablesSlot.Source>
        <StyledVariableDrawer
          service={viewVariablesModal}
          help={helpContent}
          title={t("View variable")}
          footer={null}
        >
          <VariableDetailsForm inUse={true} preview={false} />
        </StyledVariableDrawer>
        <Editor
          customToolbarButtons={renderCustomToolbarButtons()}
          language="yaml"
          theme="dark"
          title={renderTitle()}
          value={values?.[selectedLayer] || ""}
          onChange={onValuesChange}
          schema={selectedPack?.schema}
          readOnly={
            !selectedPack || selectedPack.isDisabled || allActionsDisabled
          }
          resolved={selectedPack?.resolved}
          macros={macros}
          presets={presets}
          showPresets={true}
          showVariables={true}
          presetsPathName={`presets.${selectedLayer}`}
          onPresetsChange={onPacksEditorPresetsChange}
        />
      </ReadOnlyProvider>
    </Context.Provider>
  );
}

const PackValuesEditorConnected = connect((state) => ({
  isSecurityMode: isSecurityModeFips(state),
}))(PackValuesEditor);

const filtersProps = {
  "add-on": { displayedFields: ["search"], hiddenProfileTypes: [] },
  system: { displayedFields: ["search"], hiddenProfileTypes: [] },
  cluster: {
    displayedFields: ["search", "cloudTypes", "profileTypes"],
    hiddenProfileTypes: ["add-on"],
  },
  infra: {
    displayedFields: ["search", "cloudTypes", "profileTypes"],
    hiddenProfileTypes: ["add-on"],
  },
};

function ModalContent({
  selectedProfileType,
  profileGuidToBeReplaced,
  selectedProfile,
  selectedAddons,
  loading,
}) {
  const { t } = useTranslation();
  const { profileSelectorModal, ListingModule, guid, state } =
    useContext(ProfileStackContext);
  const { selectProfile, selectAddons } =
    useContext(ProfileStackContext).actions;
  const columns = useClusterProfileColumns({
    withMenu: false,
    areSystemType: false,
    isSecurityModeFips: isSecurityModeFips(store.getState()),
  });

  const filterProps = filtersProps[selectedProfile?.type || "add-on"];
  const profileTypesHref = {
    "add-on": "cluster",
    system: "system",
  };

  if (!selectedProfileType) {
    return null;
  }

  let rowSelection = {
    type: "radio",
    selectedRowKeys: [selectedProfile],
    getCheckboxProps: (record) => {
      return {
        disabled: !isProfileValid(record),
        name: record.name,
      };
    },
    onSelect: (record) => {
      selectProfile(record.guid);
    },
  };

  let onRowClick = (record) => {
    const isValid = isProfileValid(record);

    if (!isValid) {
      return;
    }
    selectProfile(record.guid);
  };

  if (selectedProfileType === "add-on" && profileGuidToBeReplaced) {
    rowSelection.getCheckboxProps = (record) => {
      if (record.guid === profileGuidToBeReplaced) {
        return record;
      }

      return {
        ...record,
        disabled: (state?.profiles || []).find(
          (profile) =>
            profile.profile?.guid === record.guid ||
            profile.guid === record.guid
        ),
      };
    };
  }

  if (selectedProfileType === "add-on" && !profileGuidToBeReplaced) {
    rowSelection = {
      type: "checkbox",
      hideSelectAll: true,
      selectedRowKeys: selectedAddons,
      onSelect: undefined,
      onChange: selectAddons,
      getCheckboxProps: (record) => ({
        ...record,
        disabled: (state?.profiles || []).find(
          (profile) =>
            profile.profile?.guid === record.guid ||
            profile.guid === record.guid
        ),
      }),
    };

    onRowClick = (record) => {
      const isValid = isProfileValid(record);

      if (!isValid) {
        return;
      }

      if (!selectedAddons.includes(record.guid)) {
        selectAddons([...selectedAddons, record.guid]);
        return;
      }
      selectAddons(selectedAddons.filter((guid) => guid !== record.guid));
    };
  }

  return (
    <StyledDrawer
      service={profileSelectorModal}
      title={t("Choose a Profile")}
      width="1032px"
      loading={loading}
      onClose={() => {
        selectedProfileType === "add-on" &&
          selectAddons(
            selectedAddons.filter((key) =>
              state?.profiles.find(
                (profile) =>
                  key === profile.profile?.guid || key === profile.guid
              )
            )
          );
        profileSelectorModal.close();
      }}
      disabledConfirm={
        profileGuidToBeReplaced && profileGuidToBeReplaced === selectedProfile
      }
    >
      <ModalContentWrapper ref={modalContentRef}>
        <ListingModule module={guid} preventInitOnMount>
          <FiltersForm {...filterProps} top="0px" />
          <Blocks.Table
            locale={{
              emptyText: (
                <>
                  <span>{t("No profiles available.")}</span>{" "}
                  <Link
                    to={`/profiles/${
                      profileTypesHref[selectedProfileType] || "cluster"
                    }/create`}
                    onClick={() => profileSelectorModal.close()}
                  >
                    {t("Create one now")}
                  </Link>
                </>
              ),
            }}
            columns={columns}
            data-qa="cluster-addons-selector-table"
            rowSelection={rowSelection}
            onRowClick={onRowClick}
          />
          <Blocks.Pagination bottom="0px" isSticky={false} />
        </ListingModule>
      </ModalContentWrapper>
    </StyledDrawer>
  );
}

const ConnectedProfileSelectorModal = connect((state, ownProps) => {
  const { profileSelectorModal } = ownProps.module;
  return {
    ...ownProps.module.state,
    selectedProfileType: profileSelectorModal?.data?.profileType,
    profileGuidToBeReplaced:
      profileSelectorModal?.data?.profileGuidToBeReplaced,
  };
})(ModalContent);

const EmptyProfiles = ({ options }) => {
  const { t } = useTranslation();
  const { addProfile } = useContext(ProfileStackContext).actions;
  return (
    <EmptyProfilesContent>
      {options.showClusterProfileEmptyInstructions ? (
        <ClusterProfileEmptyInstructions
          addProfile={addProfile(
            options.mandatoryProfileTypes,
            options.cloudType
          )}
        />
      ) : (
        <>
          <SidebarTitle>{t("Cluster profiles")}</SidebarTitle>
          <EmptyPageWrap>
            <EmptyPage
              title={t("No profiles to display")}
              subtitle={t("Attach a profile to this cluster")}
              extraContent={
                <StyledAttachButton
                  data-qa="attach-profile"
                  onClick={addProfile()}
                >
                  {t("Attach Profile")}
                </StyledAttachButton>
              }
            />
          </EmptyPageWrap>
        </>
      )}
    </EmptyProfilesContent>
  );
};

const MainWrap = styled.div`
  display: flex;
  flex-grow: 1;
  width: 100%;
  overflow: hidden;
  height: 100%;
`;

const ProfileVariablesFormWrap = styled.div`
  height: 100%;
  overflow: auto;
  padding: 24px 32px;
`;

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InvalidMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${error};
`;

const StyledAccordion = styled(Accordion)`
  max-width: 800px;

  .ant-collapse-item {
    background: ${elevationSurfaceSubtlest};
    margin-bottom: 16px;
  }

  .ant-collapse-item > .ant-collapse-header {
    height: 48px;
    background: ${elevationSurfaceSubtlest};
    border-bottom: none;
    box-shadow: none;

    font-size: 14px;
    text-transform: none;
    color: ${lightNeutralNeutral1000};
  }

  .ant-collapse-item .ant-collapse-content-box {
    padding: 8px;
  }
`;

const VariablesFormWrap = styled.div`
  border-radius: 4px;
  background: ${textInverse};
  border: 1px solid ${borderColorDefault};
  padding: 12px 16px;
  margin: 20px 0;
`;

const FieldWrapper = styled.div`
  display: flex;
  align-items: center;

  > ${FieldWrap} {
    width: 428px;
    margin-bottom: 0;
  }
`;

export function VariablesForm({
  prefix = "variables",
  profilesWithErrors = [],
  groupedVariables = [],
  expanded,
  setExpanded,
}) {
  const { data: formData, onFieldChange } = useFormContext();

  const renderItem = ({ profile, variables }) => {
    if (!variables.length) {
      return null;
    }

    const hasErrors = profilesWithErrors.includes(profile.uid);

    return (
      <Accordion.Panel
        key={profile.uid}
        value={profile.uid}
        header={
          <AccordionHeader>
            <HeaderTitle>
              {PROFILE_TYPE_ICONS[profile.type]}
              <span>
                {profile.name} {profile.version}
              </span>
            </HeaderTitle>

            {hasErrors ? (
              <InvalidMessage>
                <span>Invalid</span>
                <Icon awesome={faExclamationCircle} />
              </InvalidMessage>
            ) : null}
          </AccordionHeader>
        }
      >
        <VariablesFormWrap>
          {variables.map((variable) => {
            const fieldValue = formData?.variables?.[variable?.guid]?.value;
            return (
              <FieldWrapper key={variable?.guid}>
                <Fields.Dynamic
                  {...getFieldPropsFromVariable({
                    prefix,
                    variable,
                    fieldValue,
                    onUseDefaultValue: () =>
                      onFieldChange(
                        `variables.${variable?.guid}.value`,
                        variable.defaultValue
                      ),
                  })}
                />
              </FieldWrapper>
            );
          })}
        </VariablesFormWrap>
      </Accordion.Panel>
    );
  };

  if (isEmpty(formData?.variables)) {
    return <Loader />;
  }
  return (
    <StyledAccordion
      variant="white"
      activeKey={expanded}
      onChange={setExpanded}
    >
      {groupedVariables.map(renderItem)}
    </StyledAccordion>
  );
}

function VariablesEditor() {
  const { profiles, variablesErrors } = useContext(ProfileStackContext).state;
  const { variableFormActions, variableFormModule: moduleName } =
    useContext(ProfileStackContext);
  const [expanded, setExpanded] = useState();

  const groupedVariables = useMemo(
    () => getGroupedVariables(profiles),
    [profiles]
  );

  useEffect(() => {
    setExpanded(groupedVariables.map((variable) => variable?.profile?.uid));
  }, [groupedVariables]);

  const profilesWithErrors = useMemo(() => {
    if (!variablesErrors?.length) {
      return [];
    }
    return getProfilesWithErrors(groupedVariables, variablesErrors);
  }, [groupedVariables, variablesErrors]);

  const VariableForm = useMemo(() => {
    return createForm({
      actions: variableFormActions,
      Component: VariablesForm,
    });
  }, [variableFormActions]);

  return (
    <ProfileVariablesFormWrap>
      <VariableForm
        module={moduleName}
        preventInitOnMount
        profilesWithErrors={profilesWithErrors}
        groupedVariables={groupedVariables}
        expanded={expanded}
        setExpanded={setExpanded}
      />
    </ProfileVariablesFormWrap>
  );
}

function ProfileStack({
  module,
  hasState,
  hasProfiles,
  options,
  isSecurityMode,
  clusterUid,
  openClusterEditModal,
  hasChanges,
  mode,
}) {
  if (!hasState) {
    return <Loader />;
  }

  return (
    <ProfileStackContext.Provider
      value={{ ...module, state: module.state, mode }}
    >
      {!hasProfiles ? (
        <EmptyProfiles options={options} />
      ) : (
        <MainWrap>
          <ProfileList
            isSecurityMode={isSecurityMode}
            clusterUid={clusterUid}
            openClusterEditModal={openClusterEditModal}
            hasChanges={hasChanges}
          />
          <PackValuesEditorConnected />
        </MainWrap>
      )}
      <ConnectedProfileSelectorModal module={module} />
    </ProfileStackContext.Provider>
  );
}

ProfileStack.Drawer = function ProfileSelectionDrawer({ module }) {
  if (!module?.state) {
    return <Loader />;
  }

  return (
    <ProfileStackContext.Provider value={{ ...module, state: module.state }}>
      <ConnectedProfileSelectorModal module={module} />
    </ProfileStackContext.Provider>
  );
};

ProfileStack.Variables = function Variables({ module }) {
  if (!module?.state) {
    return <Loader />;
  }

  return (
    <ProfileStackContext.Provider value={{ ...module, state: module.state }}>
      <VariablesEditor />
    </ProfileStackContext.Provider>
  );
};

const ConnectedModule = connect(
  (state, ownProps) => {
    return {
      hasState: !!ownProps.module.state,
      hasProfiles: ownProps.module.state?.profiles?.length > 0,
      isSecurityMode: isSecurityModeFips(state),
      ...ownProps.module.state,
    };
  },
  { openClusterEditModal }
)(ProfileStack);

export default ConnectedModule;
